<template>
  <div
    class="AppCalendarDropdown"
    :class="{ 'AppCalendarDropdown--readonly': isReadonly }"
    v-click-outside="closeDropdown"
    ref="root"
  >
    <vc-date-picker
      v-model="innerValue"
      @input="setDropdownDate($event)"
      class="app-date-picker"
      :model-config="modelConfig"
      :min-date="minDate"
      :max-date="maxDate"
      :popover="{ visibility: 'hidden' }"
      :mode="mode"
      :update-on-input="false"
      :is-range="false"
      ref="mainCalendar"
      is24hr
    >
      <template v-slot="{ inputValue, inputEvents, updateValue }">
        <label
          class="AppCalendarDropdown__main"
          :class="{
            'AppCalendarDropdown__main--focused': isFocused,
          }"
          @click="openDropdown()"
        >
          <app-title-box
            v-if="title"
            class="AppCalendarDropdown__title"
            size="small"
            :title="title"
            :isTooltip="!!tooltip"
            :tooltip="tooltip"
            :isRequired="isRequired"
          ></app-title-box>

          <input
            class="AppCalendarDropdown__input"
            type="text"
            :placeholder="innerPlaceholder"
            @focus="handleFocus"
            @blur="handleBlur"
            @change="
              updateValue($event.target.value, {
                hidePopover: false,
                formatInput: true,
              })
            "
            v-on="inputEvents"
            :value="inputValue"
            :disabled="isDisabled"
            :readonly="isReadonly"
            ref="input"
          />
          <app-svg-icon
            v-if="isIcon"
            isCurrentColor
            class="AppCalendarDropdown__icon"
            name="calendar"
          ></app-svg-icon>
        </label>
      </template>
    </vc-date-picker>

    <slide-y-up-transition>
      <div
        v-show="isOpen"
        class="AppCalendarDropdown__dropdown"
        ref="dropdownBody"
      >
        <div class="AppCalendarDropdown__dropdown-header">
          <h3 class="AppCalendarDropdown__dropdown-header-title">
            Укажите дату и время
          </h3>
          <button
            @click="closeDropdown"
            class="AppCalendarDropdown__dropdown-header-close"
          ></button>
        </div>
        <vc-date-picker
          v-model="innerValue"
          class="app-date-picker AppCalendarDropdown__dropdown-datepicker"
          :model-config="modelConfig"
          :min-date="minDate"
          :max-date="maxDate"
          :popover="{ visibility: 'click' }"
          mode="date"
          :update-on-input="false"
          :is-range="false"
          is24hr
          ref="dropdownDatePicker"
          :columns="1"
        >
        </vc-date-picker>
        <div class="AppCalendarDropdown__dropdown-footer">
          <app-button-control
            v-if="isPerpetualBtn"
            @click="setPerpetual()"
            class="AppCalendarDropdown__dropdown-footer-btn"
            :isDisabled="isCurrentPerpetual"
            :class="{
              'AppCalendarDropdown__dropdown-footer-btn--active': isCurrentPerpetual,
            }"
          >
            Бессрочно
          </app-button-control>
          <app-button-control
            @click="
              nextDate({
                weeks: -1,
              })
            "
            class="AppCalendarDropdown__dropdown-footer-btn"
            :class="{
              'AppCalendarDropdown__dropdown-footer-btn--full': !isPerpetualBtn,
            }"
          >
            Неделя
          </app-button-control>
          <app-button-control
            @click="
              nextDate({
                months: -1,
              })
            "
            class="AppCalendarDropdown__dropdown-footer-btn"
            :class="{
              'AppCalendarDropdown__dropdown-footer-btn--full': !isPerpetualBtn,
            }"
          >
            Месяц
          </app-button-control>

          <app-title-box
            class="AppCalendarDropdown__title"
            size="small"
            title="Укажите время завершения"
            :isTooltip="true"
            tooltip="Укажите время завершения"
            :isRequired="true"
          ></app-title-box>

          <app-select-control
            openDirection="top"
            isRequired
            v-model="time"
            :options="timeOptions"
            label="label"
            track-by="time"
            title="Время"
            tooltip="Время"
            :internal-search="true"
            :preserveSearch="true"
            :placeholder="placeholderForSelect"
            :allowEmpty="true"
            @select="changeTime"
          ></app-select-control>
        </div>
      </div>
    </slide-y-up-transition>
  </div>
</template>

<script>
import Cleave from 'cleave.js';
import { SlideYUpTransition } from 'vue2-transitions';
import { setPositionLeftDropdown, dateFormat } from '@/helpers';
import { parseISO, sub, set } from 'date-fns';

export default {
  name: 'AppCalendarDropdown',
  inject: ['responsive'],
  components: {
    AppSvgIcon: () => import('@/components/common/AppSvgIcon'),
    AppTitleBox: () => import('@/components/common/AppTitleBox'),
    AppButtonControl: () => import('@/components/ui/AppButtonControl'),
    AppSelectControl: () => import('@/components/ui/AppSelectControl'),
    SlideYUpTransition,
  },
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    isPerpetualBtn: {
      type: Boolean,
    },
    type: {
      type: String,
      default: '',
    },
    isReadonly: {
      type: Boolean,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
      default: false,
    },
    isIcon: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'dateTime',
    },
    tooltip: {
      type: String,
      default: '',
    },
    maxDate: {
      default: undefined,
    },
    minDate: {
      default: undefined,
    },
    title: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Date, Object],
      default: '',
    },
    modelConfig: {
      type: Object,
      default() {
        return {
          type: 'string',
          mask: 'YYYY-MM-DD HH:mm:ss',
          timeAdjust: '08:00:00',
        };
      },
    },
  },
  data: () => ({
    isFocused: false,
    isOpen: false,
    innerValue: '',
    innerType: '',
    innerPlaceholder: '',
    time: {
      label: 'Начало рабочего дня (08:00)',
      time: '08:00:00',
    },
  }),
  computed: {
    placeholderForSelect() {
      return this.timeValue ? this.timeValue.slice(0, -3) : 'Не выбрано время';
    },
    dateValue() {
      if (!this.value) {
        return null;
      }
      return this.value.split(' ')[0];
    },
    timeValue() {
      if (!this.value) {
        return null;
      }
      return this.value.split(' ')[1];
    },
    isCurrentPerpetual() {
      return this.innerType === 'perpetual';
    },
    timeOptions() {
      return [
        {
          label: 'Начало рабочего дня (08:00)',
          time: '08:00:00',
          $isDisabled:
            (this.minDate
              ? new Date(this.minDate) >= new Date(`${this.dateValue} 08:00:00`)
              : false) ||
            (this.maxDate
              ? new Date(this.maxDate) <= new Date(`${this.dateValue} 08:00:00`)
              : false),
        },
        {
          label: 'До обеда (12:00)',
          time: '12:00:00',
          $isDisabled:
            (this.minDate
              ? new Date(this.minDate) >= new Date(`${this.dateValue} 12:00:00`)
              : false) ||
            (this.maxDate
              ? new Date(this.maxDate) <= new Date(`${this.dateValue} 12:00:00`)
              : false),
        },
        {
          label: 'После обеда (14:00)',
          time: '14:00:00',
          $isDisabled:
            (this.minDate
              ? new Date(this.minDate) >= new Date(`${this.dateValue} 14:00:00`)
              : false) ||
            (this.maxDate
              ? new Date(this.maxDate) <= new Date(`${this.dateValue} 14:00:00`)
              : false),
        },
        {
          label: 'Конец рабочего дня (18:00)',
          time: '18:00:00',
          $isDisabled:
            (this.minDate
              ? new Date(this.minDate) >= new Date(`${this.dateValue} 18:00:00`)
              : false) ||
            (this.maxDate
              ? new Date(this.maxDate) <= new Date(`${this.dateValue} 18:00:00`)
              : false),
        },
        {
          label: 'Полночь (24:00)',
          time: '24:00:00',
          $isDisabled:
            (this.minDate
              ? new Date(this.minDate) >= new Date(`${this.dateValue} 24:00:00`)
              : false) ||
            (this.maxDate
              ? new Date(this.maxDate) <= new Date(`${this.dateValue} 24:00:00`)
              : false),
        },
      ];
    },
  },
  watch: {
    value: {
      immediate: false,
      handler(value) {
        this.innerValue = value;
        if (this.timeValue) {
          const timeObject = this.timeOptions.find(
            (el) => el.time === this.timeValue
          );
          this.time = timeObject || null;
        }
      },
    },
    innerValue: {
      immediate: false,
      deep: true,
      handler(value) {
        let date = '';

        if (!value && this.isCurrentPerpetual) {
          date = null;
        } else if (value) {
          this.innerType = 'date';
          this.innerPlaceholder = this.placeholder;

          date = this.checkDate(value);
          if (!date) {
            return;
          }
        } else if (!value) {
          this.innerType = 'empty';
          this.innerPlaceholder = this.placeholder;
          date = null;
        }
        this.$emit('input', date);
        this.$emit('type', this.innerType);
      },
    },
  },
  created() {
    this.innerValue = this.value;
    this.innerType = this.type;
    this.innerPlaceholder = this.isCurrentPerpetual
      ? 'Бессрочно'
      : this.placeholder;
    const timeObject = this.timeOptions.find(
      (el) => el.time === this.timeValue
    );
    this.time = timeObject || null;
  },
  // TODO: сделать автоматическое позиционирование дропдауна
  mounted() {
    window.addEventListener('resize', this.changePositionDropdown);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const c = new Cleave(this.$refs.input, {
      delimiters: ['.', '.', ' ', ':'],
      blocks: [2, 2, 4, 2, 2],
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.changePositionDropdown);
  },
  methods: {
    setPositionLeftDropdown,
    /**
     * Проверка времени
     * @param date - дата
     */
    checkDate(date) {
      const checkedMinDate = new Date(this.minDate) < new Date(date);
      const checkedMaxDate = this.maxDate
        ? new Date(this.maxDate) > new Date(date)
        : true;
      if (!checkedMaxDate) {
        this.$nextTick(() => {
          this.innerValue = dateFormat(
            set(sub(new Date(parseISO(this.maxDate)), { hours: 1 }), {
              minutes: 0,
              seconds: 0,
            }),
            'yyyy-MM-dd HH:mm:ss'
          );
        });
        return false;
      }
      if (!checkedMinDate) {
        this.$nextTick(() => {
          if (this.minDate instanceof Date) {
            this.innerValue = dateFormat(
              set(sub(this.minDate, { hours: -1 }), {
                minutes: 0,
                seconds: 0,
              }),
              'yyyy-MM-dd HH:mm:ss'
            );
          } else {
            this.innerValue = dateFormat(
              set(sub(new Date(parseISO(this.minDate)), { hours: -1 }), {
                minutes: 0,
                seconds: 0,
              }),
              'yyyy-MM-dd HH:mm:ss'
            );
          }
        });
        return false;
      }
      return dateFormat(date, 'yyyy-MM-dd HH:mm:ss');
    },
    /**
     * Смещение даты , указать объект {
     * years: 2,
     * months: 9,
     * weeks: 1,
     * days: 7,
     * hours: 5,
     * minutes: 9,
     * seconds: 30
     * }
     */
    nextDate(value) {
      this.innerPlaceholder = this.placeholder;
      if (this.innerValue) {
        this.innerValue = dateFormat(
          sub(new Date(parseISO(this.innerValue)), value),
          'yyyy-MM-dd HH:mm:ss'
        );
      } else {
        const date = dateFormat(sub(new Date(), value), 'yyyy-MM-dd');
        this.innerValue = `${date} 08:00`;
      }
      this.setDropdownDate();
    },
    /**
     * Установка бессрочности
     */
    setPerpetual() {
      const { dropdownDatePicker } = this.$refs;
      dropdownDatePicker.move(new Date());
      this.innerType = 'perpetual';
      this.innerValue = null;
      this.time = null;
      this.innerPlaceholder = 'Бессрочно';
      this.closeDropdown();
    },
    /**
     * Изменение времени
     */
    changeTime(event) {
      if (!this.dateValue) {
        const date = dateFormat(new Date(), 'yyyy-MM-dd');
        this.innerValue = `${date} ${event.time}`;
      } else {
        this.innerValue = `${this.dateValue} ${event.time}`;
      }
      this.innerType = 'date';
    },
    /**
     * Измениение позиции дропдауна
     */
    changePositionDropdown() {
      if (this.isOpen) {
        this.$refs.dropdownBody.style.left = this.setPositionLeftDropdown(
          this.$refs.root,
          this.$refs.dropdownBody
        );
      }
    },
    /**
     * Очистка датапикера
     */
    clearDate() {
      this.innerValue = null;
      const { dropdownDatePicker } = this.$refs;
      dropdownDatePicker.move(new Date());
      this.closeDropdown();
    },
    /**
     * Установка значения датапикера в дропдауне
     * @param value
     */
    setDropdownDate() {
      this.$nextTick(() => {
        if (this.dateValue) {
          const { dropdownDatePicker } = this.$refs;
          dropdownDatePicker.move(this.dateValue);
        }
      });
    },
    /**
     * Открытие дропдауна
     */
    openDropdown() {
      this.isOpen = true;
      this.$nextTick(() => {
        this.changePositionDropdown();
      });
      this.$emit('open');
    },
    /**
     * Закрытие дропдауна
     */
    closeDropdown() {
      this.isOpen = false;
      this.$emit('close');
    },
    /**
     * Действие на событие change
     * @param event
     */
    handleChange(event) {
      this.$emit('change', event);
    },
    /**
     * Действие на событие blur
     * @param event
     */
    handleBlur(event) {
      this.isFocused = false;
      this.innerPlaceholder = this.isCurrentPerpetual
        ? 'Бессрочно'
        : this.placeholder;
      this.$emit('blur', event);
    },
    /**
     * Действие на событие focus
     * @param event
     */
    handleFocus(event) {
      this.isFocused = true;
      this.innerPlaceholder = this.placeholder;
      this.$emit('focus', event);
    },
  },
};
</script>

<style lang="scss" scoped>
.AppCalendarDropdown {
  $root: &;

  position: relative;
  width: 100%;

  &__main {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 7px 12px;
    cursor: pointer;
    background: $white;
    border: 1px solid $gray-light;
    border-radius: 2px;
    transition: border $A;

    &:hover {
      border-color: $color-brand-green;
    }

    &--focused {
      border-color: $color-brand-green;
    }
  }

  &__title {
    margin-bottom: 8px;
  }

  &__range-wrapper {
    display: flex;
  }

  &__range-delimiter {
    padding: 0 5px;
    font-size: rem(14);
    font-weight: 300;
    line-height: 18px;
  }

  &__range-input {
    padding: 0;
    font-size: rem(14);
    font-weight: 300;
    line-height: 18px;
    color: $black;
    letter-spacing: 0.02em;
    cursor: pointer;
    background-color: $color-transparent;
    border: 0;

    &::placeholder {
      color: $gray-dark;
    }
  }

  &__input {
    padding: 0;
    font-size: rem(14);
    font-weight: 300;
    line-height: 22px;
    color: $black;
    letter-spacing: 0.02em;
    cursor: pointer;
    background-color: $color-transparent;
    border: 0;

    &::placeholder {
      color: $gray-dark;
    }

    &:disabled {
      background: $white;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px $color-white inset !important;
    }
  }

  &-icon {
    position: absolute;
    top: 50%;
    right: 20px;
    width: 12px;
    height: 13px;
    stroke-width: 0;
    transform: translateY(-50%);
  }

  &__dropdown {
    @include main-container-box;

    position: absolute;
    top: 62px;
    z-index: 5;
    width: 335px;
    padding: 0 20px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 2px;
    box-shadow: 0 20px 20px rgba(0, 0, 0, 0.25);

    @include media-xs() {
      width: 311px;
    }

    @include media-xxs() {
      right: 0;
      left: auto;
    }
  }

  &__dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 69px;
    border-bottom: 1px solid #eee;
  }

  &__dropdown-header-title {
    margin: 0;
    font-size: rem(13);
    font-weight: 300;
    line-height: 16px;
    color: $black;
  }

  &__dropdown-header-close {
    position: relative;
    right: -8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    color: $color-white;
    cursor: pointer;
    background-color: $color-transparent;
    border: 0;
    transition: color $A;

    &::before,
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 13px;
      height: 1px;
      content: '';
      background: rgba($color: $black, $alpha: 0.3);
      transition: background-color $A;
      transform: translate(-50%, -50%) rotate(45deg);
    }

    &::after {
      transform: translate(-50%, -50%) rotate(-45deg);
    }

    &:hover {
      &::before,
      &::after {
        background: rgba($color: $black, $alpha: 1);
      }
    }
  }

  &__dropdown-footer {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 1px solid #eee;
  }

  &__dropdown-footer-btn {
    // width: 133px;
    padding: 0 15px;
    // margin-right: 10px;
    margin-bottom: 8px;

    &--full {
      flex-grow: 1;
      margin-right: 10px;
    }

    &:last-of-type {
      margin-right: 0;
    }

    &--active {
      cursor: default;
      background: $color-tag-filter-bg !important;
      border-color: transparent;

      &:hover {
        background: $color-tag-filter-bg !important;
      }
    }
  }
}
</style>
